/**
 * @fileoverview CreateNew component for the sidebar navigation.
 *
 * This component provides a dropdown menu for creating new resources in the application.
 * It handles different types of resources including flows, connectors, projects, custom objects,
 * and various configuration items. The component also manages file uploads and imports.
 *
 * The component integrates with the permission system to show only resources that the user
 * has permission to create, and provides special handling for connector types and object templates.
 *
 * @module CreateNew
 */

import { Alert, Button, Dropdown, Menu } from "antd";
import { Buffer } from "buffer";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { Icon } from "components/Icon/Icon";
import { IconCollection } from "components/Icon/IconCollection";
import { usePendingImports } from "components/Layout/Sidebar/components/CreateNew/usePendingImports";
import ExpandableNotification from "components/Notification/ExpandableNotification";
import NotificationService from "components/Notification/Notification.service";
import { FeRecordType } from "config/config";
import {
    configurationResourcesSorted,
    createMoreResourcesSorted,
    createResources,
} from "config/resources";
import { getUserData, getUserPermissionData } from "containers/Auth/Auth.selectors";
import { getCreatableResourceList, hasCreatablePermission } from "containers/Auth/permissions";
import { getActiveOrDefaultProjectId } from "containers/Projects/Projects.selectors";
import { LocationPickerPopover } from "containers/Resources/components/CommonActions/MoveToLocation/LocationPickerPopover";
import { ImportSessionModal } from "containers/Resources/components/ImportSessionModal/ImportSessionModal";
import { StagedFile } from "containers/Resources/components/ImportSessionModal/useImportSession";
import { getLocationField } from "containers/Resources/components/CommonActions/MoveToLocation/MoveToLocationForm";
import {
    UploadGitOptions,
    useUploadContainerSettings,
    useUploadGitOptions,
} from "containers/Resources/components/CommonActions/MoveToLocation/UploadGitOptions";
import { useMoveToLocation } from "containers/Resources/components/CommonActions/MoveToLocation/useMoveToLocation";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import {
    connectorInputSchemasSelector,
    objectTemplateSchemaSelector,
} from "containers/Resources/Resources.selectors";
import { getWorkspaceLogLinkBySubject } from "containers/Resources/screens/ResourcesSingle/components/RecordLogList/utils";
import { getDateTimeAppendix } from "containers/Resources/screens/ResourcesSingle/utils";
import { actions as uploadActions } from "containers/Upload/Upload.reducer";
import { isArray } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useFilePicker } from "use-file-picker";
import { FileContent } from "use-file-picker/dist/interfaces";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import { sortAlphabeticallyByProperty } from "utils/sorting";
import { v4 as uuidv4 } from "uuid";
import "./CreateNew.scss";

/**
 * Generates a new record creation path with appropriate parameters.
 *
 * Creates a URL path for creating a new record of the specified type,
 * automatically adding a timestamp to the name and handling special cases
 * like connector types.
 *
 * @param {FeRecordType} type - The type of record to create
 * @param {Object} additionalParams - Additional URL parameters to include
 * @param {string} additionalParams.connector_type - Special parameter for connector types
 * @returns {string} The complete URL path for creating the new record
 *
 * @example
 * getNewRecordPath("flow") // Returns "/flow/new?name=flow 2024-01-15 10:30:45"
 * getNewRecordPath("connector", { connector_type: "http" }) // Returns "/connector/new?name=http connector 2024-01-15 10:30:45&connector_type=http"
 */
export const getNewRecordPath = (
    type: FeRecordType,
    additionalParams: { [key: string]: string } = {}
) => {
    const searchParams = new URLSearchParams(additionalParams);

    const customObjectName =
        type === "custom_object" && searchParams.get("object_template_name")
            ? `${searchParams.get("object_template_name")}`
            : "";
    if (customObjectName) {
        searchParams.set("name", `${customObjectName} ${getDateTimeAppendix()}`);
        searchParams.delete("object_template_name");
        return `/${type}/new?${searchParams.toString()}`;
    }
    const namePrefix =
        type === "connector" && searchParams.has("connector_type")
            ? `${searchParams.get("connector_type")} `
            : "";
    searchParams.set("name", `${namePrefix}${type} ${getDateTimeAppendix()}`);

    return `/${type}/new?${searchParams.toString()}`;
};

/**
 * Generates a path for creating a new custom object with a specific template.
 *
 * @param {string} objectTemplateId - The ID of the object template to use
 * @returns {string} The complete URL path for creating the custom object
 */
export const getNewCustomObjectPath = (objectTemplateId: string, objectTemplateName: string) =>
    getNewRecordPath("custom_object", {
        object_template_id: objectTemplateId,
        object_template_name: objectTemplateName,
    });

/** Key used for storing pending imports in the data store */
export const pendingImportsKey = "PENDING_IMPORTS";

/**
 * CreateNew component that provides a dropdown menu for creating various resources.
 *
 * This component renders a "Create" button that opens a dropdown menu with options
 * to create different types of resources. The available options are filtered based
 * on user permissions and include:
 *
 * - Primary resources (flows, connectors, projects, custom objects)
 * - Secondary resources (files, schedules, webhooks, etc.)
 * - Configuration resources (LDAP, vault, sync configs, etc.)
 * - File upload and import functionality
 *
 * The component handles special cases for connectors (which have subtypes) and
 * custom objects (which require object templates).
 *
 * @returns {JSX.Element} The CreateNew component with dropdown menu
 */
export const CreateNew = () => {
    const dispatch = useDispatch();
    const history = useHistory();

    const userData = useSelector(getUserData);
    const permissionData = useSelector(getUserPermissionData);

    // Filter available resources based on user permissions
    const { configurationResourceList, createMoreResourceList, createResourceList } =
        useMemo(() => {
            if (Object.keys(permissionData).length === 0) {
                return {
                    configurationResourceList: [],
                    createMoreResourceList: [],
                    createResourceList: [],
                };
            }
            if (hasCreatablePermission(permissionData.all)) {
                return {
                    configurationResourceList: configurationResourcesSorted,
                    createMoreResourceList: createMoreResourcesSorted,
                    createResourceList: createResources,
                };
            } else {
                return {
                    configurationResourceList: getCreatableResourceList(
                        configurationResourcesSorted,
                        permissionData
                    ),
                    createMoreResourceList: getCreatableResourceList(
                        createMoreResourcesSorted,
                        permissionData
                    ),
                    createResourceList: getCreatableResourceList(createResources, permissionData),
                };
            }
        }, [permissionData]);

    const isAllRecordListShown = useMemo(() => {
        const allRecordListLength =
            configurationResourcesSorted.length +
            createMoreResourcesSorted.length +
            createResources.length;
        const shownRecordListLength =
            configurationResourceList.length +
            createMoreResourceList.length +
            createResourceList.length;

        return allRecordListLength === shownRecordListLength;
    }, [
        configurationResourceList.length,
        createMoreResourceList.length,
        createResourceList.length,
    ]);

    const canCreateFile =
        hasCreatablePermission(permissionData.all) || hasCreatablePermission(permissionData.file);

    // Get current project and available connector/object template schemas
    const activeProjectId = useSelector(getActiveOrDefaultProjectId);

    // Location picker for create-menu file uploads: lets the user choose the
    // target container (project/bundle/workspace) the FILE records are created
    // in, instead of silently defaulting to the active project. Seeded to the
    // active project as the sensible default.
    const {
        selectedLocation: uploadLocation,
        changeValue: changeUploadLocation,
        options: uploadLocationOptions,
    } = useMoveToLocation("file", activeProjectId);
    // Files chosen via the create-menu upload, held until the user confirms the
    // destination in the upload-location dialog. null = dialog closed.
    const [pendingUploadFiles, setPendingUploadFiles] = useState<FileContent[] | null>(null);
    // File picked for interactive import, held open in the ImportSessionModal.
    // null = the import modal is closed.
    const [importFile, setImportFile] = useState<StagedFile | null>(null);
    // Git / tracked-size settings of the picked upload target + the per-upload
    // track_in_git toggle state and client-side over-limit block (only relevant
    // when the target container is git-enabled).
    const uploadContainerSettings = useUploadContainerSettings(
        uploadLocation,
        pendingUploadFiles !== null
    );
    const pendingUploadFileSizes = useMemo(
        () => (pendingUploadFiles ?? []).map((file) => file.content?.length ?? 0),
        [pendingUploadFiles]
    );
    const uploadGitOptions = useUploadGitOptions(
        uploadLocation?.value,
        uploadContainerSettings,
        pendingUploadFileSizes
    );
    const connectorInputSchemas = useSelector(connectorInputSchemasSelector);
    const connectorTypes = Object.entries(connectorInputSchemas).map(([key, value]) => ({
        key,
        label: (value as any).label,
        // Canonical/alias annotation (from the backend connector-schema payload):
        // `isAlias` marks a permanent alias name, `canonical` is the name it
        // resolves to, `aliases` are the alternative names of a canonical type.
        canonical: (value as any).canonical as string | undefined,
        aliases: ((value as any).aliases ?? []) as string[],
        isAlias: Boolean((value as any).is_alias),
    }));
    const objectTemplateSchemas = useSelector(objectTemplateSchemaSelector);
    const objectTemplateTypeList = objectTemplateSchemas.map(({ name, id, icon }) => ({
        value: id,
        label: name,
        icon,
    }));
    const objectTemplateTypeListSorted: {
        value: string;
        label: string;
        icon?: string;
    }[] = sortAlphabeticallyByProperty(objectTemplateTypeList, "label");

    /**
     * Handles file upload functionality.
     *
     * Uploads multiple files to the server, creating file resources for each.
     * Dispatches upload actions to track progress and shows notifications for
     * success/failure of individual files.
     *
     * @param {FileContent[]} fileInfos - Array of file information to upload
     * @param {AutocompleteOption} [location] - The target container the FILE
     *   records are created in (project/bundle/workspace). When omitted, the
     *   backend default applies.
     */
    const upload = (
        fileInfos: FileContent[],
        location?: AutocompleteOption,
        gitOptions?: { trackInGit?: boolean; isBundleContent?: boolean }
    ) => {
        if (!fileInfos || fileInfos.length <= 0) return;
        const uploadId = uuidv4();

        dispatch({
            type: uploadActions.START_UPLOAD,
            fileInfos,
            uploadId,
            recordType: "file",
        });

        const errors: Set<{ fileName: string; error: Error }> = new Set();
        let successUploads: string[] = [];

        // Create promises for each file upload
        const promises = fileInfos.map((fileInfo) =>
            new Promise((resolve, reject) => {
                dispatch({
                    type: resourcesActions.START_CREATE_RESOURCE,
                    promise: { resolve, reject },
                    resourceName: "file",
                    variables: {
                        ...getLocationField(location),
                        name: fileInfo.name,
                        content: Buffer.from(fileInfo.content, "binary").toString("base64"),
                        // Only override track_in_git when the target is git-enabled
                        // and the dialog offered the choice; otherwise let the
                        // backend apply the container default.
                        ...(gitOptions?.trackInGit !== undefined
                            ? { track_in_git: gitOptions.trackInGit }
                            : {}),
                        // Only override is_bundle_content when the target is a
                        // bundle and the dialog offered the choice; otherwise let
                        // the backend apply the bundle default.
                        ...(gitOptions?.isBundleContent !== undefined
                            ? { is_bundle_content: gitOptions.isBundleContent }
                            : {}),
                    },
                });
            })
                .then((resource: any) => {
                    successUploads.push(fileInfo.name);
                    dispatch({
                        type: uploadActions.FILE_UPLOADED,
                        uploadId,
                        recordId: resource.id,
                        recordName: resource.name,
                        fileName: fileInfo.name,
                    });
                })
                .catch((error) => {
                    errors.add({ fileName: fileInfo.name, error: error });
                    dispatch({
                        uploadId,
                        type: uploadActions.FILE_UPLOAD_FAILED,
                        fileName: fileInfo.name,
                    });
                })
        );

        // Handle completion of all uploads
        Promise.allSettled(promises).then(() => {
            dispatch({
                type: uploadActions.UPLOAD_FINISHED,
                uploadId,
            });

            // Show error notifications for failed uploads
            if (errors.size > 0) {
                for (const error of errors) {
                    const displayError =
                        typeof error.error === "object"
                            ? `${
                                  (error.error as any).title
                                      ? `${(error.error as any).title}: `
                                      : ""
                              }${error.error.message}`
                            : error.error;
                    NotificationService.warn(
                        <ExpandableNotification
                            title={`${error.fileName} failed to upload`}
                            details={displayError}
                        />
                    );
                    successUploads = successUploads.filter((value) => value !== error.fileName);
                }
            }

            // Show success notification for successful uploads
            if (successUploads.length > 0) {
                NotificationService.success(
                    <ExpandableNotification
                        title={`Upload finished`}
                        subtitle={`${successUploads.length} file(s) uploaded`}
                        details={`${successUploads.join(", ")}`}
                    />
                );
            }
        });
    };

    /**
     * Handles file import functionality.
     *
     * Stores file information for pending imports and redirects to workspace logs
     * where the import process will be handled.
     *
     * @param {FileContent[]} fileInfos - Array of file information to import
     */
    const uploadAndImport = (fileInfos) => {
        if (!fileInfos || fileInfos.length <= 0) return;

        // Store files for pending import
        dispatch({
            type: dataStoreAction.STORE_DATA,
            key: pendingImportsKey,
            data: fileInfos,
        });

        // Redirect to workspace logs for import processing
        const workspaceLogsLink = getWorkspaceLogLinkBySubject(userData?.workspace_id, "import");
        history.push(workspaceLogsLink);
    };

    usePendingImports();

    const [openFileSelector, { filesContent, loading }] = useFilePicker({
        accept: "*",
        readAs: "BinaryString",
    });

    // TODO (medium) refactor: this is not a clean solution, however, using two useFilePickers does not work as it should. alternative solution: find better library
    const [isImporting, setIsImporting] = useState(false);

    // Handle file content changes (upload or import based on isImporting state)
    useEffect(() => {
        if (!filesContent || filesContent.length <= 0) return;
        if (isImporting) {
            // Open the interactive import modal with the picked file (upload once
            // -> preflight -> resolve -> retry), replacing the old stash-file +
            // navigate-to-logs + fire-and-forget POST /import flow.
            const picked = filesContent[0];
            setImportFile({ name: picked.name, content: picked.content });
        } else {
            // Defer the actual upload until the user picks a destination in the
            // upload-location dialog. Clear the premature "upload in progress"
            // spinner that the file-picker `loading` effect raised while reading
            // the files, and reset the picker to the active-project default.
            dispatch({ type: uploadActions.UPLOAD_FINISHED, uploadId: null });
            changeUploadLocation(activeProjectId);
            setPendingUploadFiles(filesContent);
        }
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filesContent]);

    /** Confirm the create-menu upload: upload the pending files to the chosen location. */
    const confirmUpload = () => {
        if (pendingUploadFiles) {
            const gitOptions =
                uploadContainerSettings?.isGitEnabled || uploadContainerSettings?.isBundle
                    ? {
                          ...(uploadContainerSettings?.isGitEnabled
                              ? { trackInGit: uploadGitOptions.trackInGit }
                              : {}),
                          ...(uploadContainerSettings?.isBundle
                              ? { isBundleContent: uploadGitOptions.isBundleContent }
                              : {}),
                      }
                    : undefined;
            upload(pendingUploadFiles, uploadLocation, gitOptions);
        }
        setPendingUploadFiles(null);
    };

    /** Dismiss the upload-location dialog without uploading. */
    const cancelUpload = () => {
        setPendingUploadFiles(null);
    };

    // Handle loading state for file picker
    useEffect(() => {
        if (loading) {
            dispatch({
                type: uploadActions.REQUEST_UPLOAD,
            });
        }
        // TODO (low) evaluate alternative fixes, exhaustive adding of deps seems unclean, esp. with additional noise of useWrapper etc.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading]);

    // Menu item keys for file operations
    const uploadFileKey = "upload.file";
    const importFileKey = "import.file";

    /**
     * Handles clicks on submenu items (connectors and object templates).
     *
     * Extracts the type from the menu key and navigates to the appropriate
     * creation page with the correct parameters.
     *
     * @param {string} type - The type of submenu ("connector" or "objectTemplate")
     * @param {string} key - The menu key containing the specific type
     */
    const handleSubmenuClick = useCallback(
        (type, key) => {
            const regexStr = `${type}.(.*)`;
            const typeKeyRegexp = new RegExp(regexStr, "g");
            const typeMatches = typeKeyRegexp.exec(key);
            const foundType =
                isArray(typeMatches) && typeMatches.length >= 2 ? typeMatches[1] : null;

            if (foundType == null) return;
            const isObjectTemplate = type === "objectTemplate";

            const foundObjectTemplate = isObjectTemplate
                ? objectTemplateTypeListSorted.find(({ label }) => foundType === label)
                : null;
            const path = foundObjectTemplate
                ? getNewCustomObjectPath(foundObjectTemplate?.value, foundObjectTemplate?.label)
                : getNewRecordPath("connector", { connector_type: foundType });
            history.push(path);
        },
        [objectTemplateTypeListSorted, history]
    );

    /**
     * Main click handler for all menu items.
     *
     * Routes clicks to appropriate handlers based on the menu key:
     * - File upload/import operations
     * - Submenu items (connectors, object templates)
     * - Regular resource creation
     *
     * @param {Object} event - The click event object
     * @param {string} event.key - The key of the clicked menu item
     */
    const clickHandler = (event) => {
        const { key } = event;
        if (key === undefined) {
            return;
        }

        // Handle file operations
        if (key === uploadFileKey) {
            setIsImporting(false);
            openFileSelector();
            return;
        }
        if (key === importFileKey) {
            setIsImporting(true);
            openFileSelector();
            return;
        }

        // Handle submenu items
        if (event.key.includes("connector.")) {
            handleSubmenuClick("connector", event.key);
        } else if (event.key === "connectorSubmenu" || event.key === "custom_objectSubmenu") {
            // do nothing - these are submenu titles
        } else if (event.key.includes("objectTemplate.")) {
            handleSubmenuClick("objectTemplate", event.key);
        } else {
            // Handle regular resource creation
            history.push(getNewRecordPath(event.key));
        }
    };

    // Build the dropdown menu structure
    const menu = (
        <Menu onClick={clickHandler} className="createNewMenu" triggerSubMenuAction="hover">
            {/* Primary creation resources */}
            {createResourceList?.map((resource) => {
                // Special handling for Flow - direct menu item
                if (resource?.resourceName.toLowerCase() === "flow") {
                    return (
                        <Menu.Item
                            className={`createNew${resource?.resourceName}`}
                            data-testid={`createNew${resource?.resourceName}`}
                            key={resource?.resourceName}
                            icon={<Icon icon={resource?.icon} />}
                        >
                            {resource?.label}
                        </Menu.Item>
                    );
                }

                // Special handling for Connector - submenu with connector types
                if (resource?.resourceName.toLowerCase() === "connector") {
                    return (
                        <Menu.SubMenu
                            title="Connector"
                            className="createNewConnectorMore"
                            data-testid="createNewConnectorMore"
                            onTitleClick={clickHandler}
                            key={`${resource?.resourceName}Submenu`}
                            popupClassName="createNewConnector createNewSubmenu"
                            icon={<Icon icon={resource?.icon} />}
                        >
                            {connectorTypes?.map((connectorType) => {
                                // The connector-picker label is authored on the
                                // backend connector-schema payload (`value.label`).
                                // Canonical entries read "HTTP: HTTP request"; alias
                                // entries read "REST: HTTP request (alias of HTTP)",
                                // so the alias/canonical relationship is carried by
                                // the label itself — no extra annotation needed here.
                                return (
                                    <Menu.Item
                                        className={`createNew${connectorType.key}`}
                                        data-testid={`createNew${connectorType.key}`}
                                        key={`connector.${connectorType.key}`}
                                        icon={<Icon icon={resource?.icon} />}
                                    >
                                        {connectorType.label}
                                    </Menu.Item>
                                );
                            })}
                        </Menu.SubMenu>
                    );
                }

                // Special handling for Custom Object - submenu with object templates
                if (resource?.resourceName.toLowerCase() === "custom_object") {
                    return (
                        <Menu.SubMenu
                            title="Custom object"
                            className="createNewCustomObjectMore"
                            data-testid="createNewCustomObjectMore"
                            onTitleClick={clickHandler}
                            key={`${resource?.resourceName}Submenu`}
                            popupClassName="createNewSubmenu"
                            icon={<Icon icon={resource?.icon} />}
                        >
                            {objectTemplateTypeListSorted.length ? (
                                objectTemplateTypeListSorted?.map((objectTemplateType) => (
                                    <Menu.Item
                                        className={`createNew${objectTemplateType.label}`}
                                        data-testid={`createNew${objectTemplateType.label}`}
                                        key={`objectTemplate.${objectTemplateType.label}`}
                                        icon={
                                            objectTemplateType.icon ? (
                                                <img
                                                    src={objectTemplateType.icon}
                                                    alt="icon"
                                                    className="icon"
                                                />
                                            ) : (
                                                <Icon icon={resource?.icon} />
                                            )
                                        }
                                    >
                                        {objectTemplateType.label}
                                    </Menu.Item>
                                ))
                            ) : (
                                <div className="emptySubmenu">
                                    <p key="1">No data to display.</p>
                                    <p key="2">Please create an object template first.</p>
                                </div>
                            )}
                        </Menu.SubMenu>
                    );
                }

                // Default handling for other resources
                return (
                    <Menu.Item
                        className={`createNew${resource?.resourceName}`}
                        data-testid={`createNew${resource?.resourceName}`}
                        key={resource?.resourceName}
                        icon={<Icon icon={resource?.icon} />}
                    >
                        {resource?.label}
                    </Menu.Item>
                );
            })}

            {/* Secondary resources in "More" submenu */}
            {createMoreResourceList?.length > 0 && (
                <Menu.SubMenu
                    title="More"
                    className="createNewMoreButton"
                    popupClassName="createNewMoreMenu"
                    key="more"
                    icon={<Icon icon={IconCollection.plus} />}
                >
                    {createMoreResourceList.map((resource) => (
                        <Menu.Item
                            className={`createNew${resource?.resourceName}`}
                            key={resource?.resourceName}
                            icon={<Icon icon={resource?.icon} />}
                            data-testid={`createNew${resource?.resourceName}`}
                        >
                            {resource?.label}
                        </Menu.Item>
                    ))}
                </Menu.SubMenu>
            )}

            {/* Configuration resources in "Configuration" submenu */}
            {configurationResourceList?.length > 0 && (
                <Menu.SubMenu
                    title="Configuration"
                    className="createNewConfigurationButton"
                    data-testid="createNewConfigurationButton"
                    popupClassName="createNewConfigurationMenu"
                    key="configuration"
                    icon={<Icon icon={IconCollection.settingsIcon} />}
                >
                    {configurationResourceList.map((resource) => (
                        <Menu.Item
                            className={`createNew${resource?.resourceName}`}
                            key={resource?.resourceName}
                            icon={<Icon icon={resource?.icon} />}
                            data-testid={`createNew${resource?.resourceName}`}
                        >
                            {resource?.label}
                        </Menu.Item>
                    ))}
                </Menu.SubMenu>
            )}

            <Menu.Divider />

            {/* File operations */}
            {canCreateFile && (
                <Menu.Item key={uploadFileKey} icon={<Icon icon={IconCollection.uploadOutline} />}>
                    Upload file
                </Menu.Item>
            )}
            <Menu.Item key={importFileKey} icon={<Icon icon={IconCollection.fileImportOutline} />}>
                Import
                {/* fake input field for testing (see CU-861nb6vhn) */}
                <input
                    type="file"
                    id="importFile"
                    style={{ display: "none" }}
                    onChange={(file: any) => {
                        if (file?.target?.files[0]) {
                            uploadAndImport([file?.target?.files[0]]);
                        }
                    }}
                />
            </Menu.Item>

            {/* Permission info message */}
            {!isAllRecordListShown && (
                <div className="infoMessage">
                    <Alert
                        message="Only showing items you have permission to create."
                        type="info"
                    />
                </div>
            )}
        </Menu>
    );

    return (
        <div className="create-new">
            <Dropdown overlay={menu} placement="bottomCenter" trigger={["click"]}>
                <Button className="createNewButton" data-testid="createNewButton">
                    <Icon icon={IconCollection.plus} />
                    Create
                </Button>
            </Dropdown>
            <LocationPickerPopover
                isOpen={pendingUploadFiles !== null}
                title={
                    (pendingUploadFiles?.length ?? 0) === 1
                        ? "Upload file to:"
                        : `Upload ${pendingUploadFiles?.length ?? 0} files to:`
                }
                confirmLabel="Upload"
                confirmDisabled={!uploadLocation || uploadGitOptions.blockConfirm}
                onConfirm={confirmUpload}
                onCancel={cancelUpload}
                name="upload-location"
                value={uploadLocation?.value}
                changeValue={changeUploadLocation}
                options={uploadLocationOptions}
            >
                <UploadGitOptions state={uploadGitOptions} />
            </LocationPickerPopover>
            <ImportSessionModal file={importFile} onClose={() => setImportFile(null)} />
        </div>
    );
};
